import React from 'react';
import styled from 'styled-components';
import { Paper } from '@material-ui/core';
import { Table, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';

import { ImageAttributeTemplate, CardTemplateAttributeDb } from '../redux/cardTemplateAttributeDbSlice';
import { CardTemplateImageShape, CardTemplateShapeDb } from '../redux/cardTemplateShapeDbSlice';
import { FontType, FrameType } from '../redux/cardAttributeModelIndex';
import { initial } from './index';
import useOneCardTemplateValue from '../hooks/useOneCardTemplateValue';
import useCardTemplateItemDbs from '../hooks/useCardTemplateItemDbs';
import useImageData from '../hooks/useImageData';
import { getFontData } from '../general';
import CircleShape from '../card_components/CircleShape';
import RectangleShape from '../card_components/RectangleShape';
import BalloonShape from '../card_components/BalloonShape';

type CardPaperProps = {
  width: number;
  height: number;
  scale: number;
  $backgroundDisplay: boolean;
  $backgroundColor: string;
}
const CardPaper = styled(Paper)<CardPaperProps>`
  position: relative;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
  background-color: ${props => props.$backgroundDisplay ? props.$backgroundColor : '#11ffee00'};
  overflow: hidden;
  transform: scale(${props => props.scale});
  transform-origin: ${props => {
    const scaleInWidth = initial.cardWidth / props.width;
    const scaleInHeight = initial.cardHeight / props.height;
    if (scaleInWidth > scaleInHeight) return 'top center';
    else return 'top left';
  }};
`
type TextDivProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  frame: FrameType;
  $backgroundColor?: string;
} & {[P in keyof FontType]?: FontType[P]};
const TextDiv = styled.div<TextDivProps>`
  position: absolute;
  display: grid;
  left: ${props => props.x + 'px'};
  top: ${props => props.y + 'px'};
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
  overflow-wrap: anywhere;
  overflow-y: hidden;
  transform: ${props => props.rotation ? 'rotate(' + props.rotation + 'deg)' : undefined};
  transform-origin: center;
  border: ${props => {
    if (props.frame.display) return `${props.frame.thickness}px ${props.frame.color} ${props.frame.lineStyle}`;
    else return undefined;
  }};
  border-radius: ${props => {
    if (props.frame.display && props.frame.roundCorner) return `${props.frame.radius}px`;
    else return undefined;
  }};
  box-sizing: border-box;
  background-color: ${props => props.$backgroundColor};
  font-size: ${props => props.size + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  text-decoration: ${props => props.underline ? 'underline' : undefined};
  font-family: ${props => props.name};
  text-align: ${props => props.yoko};
  align-items: ${props => props.tate};
  writing-mode: ${props => props.direction};
  text-orientation: ${props => props.orientation};
`
type TableDivProps = {
  x: number;
  y: number;
  width: number;
  height: number;
}
const TableDiv = styled.div<TableDivProps>`
  position: absolute;
  left: ${props => props.x + 'px'};
  top: ${props => props.y + 'px'};
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
`
type TableAttributeNameDivProps = {
  name?: string;
  color?: string;
  fSize?: number;
  bold?: boolean;
  italic?: boolean;
}
const TableAttributeNameDiv = styled.div<TableAttributeNameDivProps>`
  height: 15px;
  overflow: hidden;
  font-size: ${props => props.fSize + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  font-family: ${props => props.name};
`
type AttributeTableCellProps = {
  name?: string;
  color?: string;
  fSize?: number;
  bold?: boolean;
  italic?: boolean;
  yoko?: 'left' | 'center' | 'right';
}
const AttributeTableCell = styled(TableCell)<AttributeTableCellProps>`
  font-size: ${props => props.fSize + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  font-family: ${props => props.name};
  text-align: ${props => props.yoko};
`
type ImageDivProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  frame: FrameType;
  $backgroundColor?: string;
}
const ImageDiv = styled.div<ImageDivProps>`
  position: absolute;
  transform: ${props => {
    if (props.width < 0) return 'translate(' + (props.x + props.width) + 'px, ' + props.y + 'px) rotate(' + props.rotation + 'deg) scaleX(-1) !important';
    return 'translate(' + props.x + 'px, ' + props.y + 'px) rotate(' + props.rotation + 'deg) !important';
  }};
  transform-origin: center;
  width: ${props => Math.abs(props.width) + 'px'};
  height: ${props => props.height + 'px'};
  border: ${props => {
    if (props.frame && props.frame.display) return `${props.frame.thickness}px ${props.frame.color} ${props.frame.lineStyle}`;
    else return undefined;
  }};
  border-radius: ${props => {
    if (props.frame && props.frame.display && props.frame.roundCorner) return `${props.frame.radius}px`;
    else return undefined;
  }};
  box-sizing: border-box;
  background-color: ${props => props.$backgroundColor};
  overflow: hidden;
`
type AttributeImgProps = {
  x: number;
  y: number;
}
const AttributeImg = styled.img<AttributeImgProps>`
  position: absolute;
  left: ${props => props.x + 'px'};
  top: ${props => props.y + 'px'};
  user-select: none;
`

const isAttribute = (obj: any): obj is CardTemplateAttributeDb => {
  return typeof obj?.cardAttributeModel === 'string';
}
const isShape = (obj: any): obj is CardTemplateShapeDb => {
  return typeof obj?.kind === 'string';
}

type RenderImageProps = {
  item: ImageAttributeTemplate | CardTemplateImageShape;
}
const RenderImage: React.FC<RenderImageProps> = ({item}) => {
  const imageData = isShape(item) ? item.image : item.defaultImage;
  const dataUrl = useImageData(imageData.imageId);
  const imgWidth = imageData.originWidth;
  const imgHeight = imageData.originHeight;
  const trimRatioWidth = Math.abs(item.width) / (imageData.trimXTo - imageData.trimXFrom);
  const trimRatioHeight = item.height / (imageData.trimYTo - imageData.trimYFrom);
  const translateX = -imageData.trimXFrom * trimRatioWidth;
  const translateY = -imageData.trimYFrom * trimRatioHeight;

  return (
    <ImageDiv x={item.x} y={item.y}
      width={item.width} height={item.height} rotation={item.rotation}
      frame={item.frame} $backgroundColor={item.background.display ? item.background.color : undefined}
    >
      <AttributeImg src={dataUrl} draggable="false" x={translateX} y={translateY}
        width={imgWidth * trimRatioWidth} height={imgHeight * trimRatioHeight} />
    </ImageDiv>
  );
}

type CommonCardTemplatePreviewProps = {
  cardTemplateId: string;
  selected?: boolean;
}
const CommonCardTemplatePreview: React.FC<CommonCardTemplatePreviewProps> = ({ cardTemplateId, selected }) => {
  const [ids, values] = useCardTemplateItemDbs(cardTemplateId);
  const card = useOneCardTemplateValue(cardTemplateId);

  if (!card) return <></>;

  const scaleInWidth = initial.cardWidth / card.defaultWidth;
  const scaleInHeight = initial.cardHeight / card.defaultHeight;
  const displayScale = Math.min(scaleInWidth, scaleInHeight);
  const borderStyle = card.frame.display ? '1px solid ' + card.frame.color : undefined;
  const selectedStyle = `${2 / displayScale}px solid #1e88e5`;

  return (
    <CardPaper style={{ border: selected ? selectedStyle : borderStyle }}
      width={card.defaultWidth} height={card.defaultHeight} scale={displayScale}
      $backgroundDisplay={card.background.display} $backgroundColor={card.background.color}
    >
      { ids.map((id, ix) => {
        const item = values[ix];
        if (isAttribute(item) && item.cardAttributeModel === 'single') {
          const { name, color, size, bold, italic, underline, direction, orientation, yoko, tate } = getFontData(item.font);
          return (
            <TextDiv key={ix} x={item.x} y={item.y} width={item.width} height={item.height}
              rotation={item.rotation} frame={item.frame} $backgroundColor={item.background.display ? item.background.color : undefined}
              size={size} bold={bold} color={color} italic={italic} underline={underline} name={name}
              yoko={yoko} tate={tate} direction={direction} orientation={orientation}>
              {item.name}
            </TextDiv>
          );
        } else if (isAttribute(item) && item.cardAttributeModel === 'multi') {
          const { name, color, size, bold, italic, underline, direction, orientation, yoko, tate } = getFontData(item.font);
          return (
            <TextDiv key={ix} x={item.x} y={item.y} width={item.width} height={item.height}
              frame={item.frame} $backgroundColor={item.background.display ? item.background.color : undefined}
              size={size} bold={bold} color={color} italic={italic} underline={underline} name={name}
              yoko={yoko} tate={tate} direction={direction} orientation={orientation}>
              {item.name}
            </TextDiv>
          );
        } else if (isAttribute(item) && item.cardAttributeModel === 'table') {
          const { name, color, size, bold, italic, yoko } = item.font;
          const columnIds = Object.keys(item.columnNames);
          const columnNames = columnIds.map(id => item.columnNames[id]);
          return (
            <TableDiv key={ix} x={item.x} y={item.y} width={item.width} height={item.height}>
              <TableAttributeNameDiv fSize={6} bold={bold} color={color} italic={italic} name={name}>
                {item.name}
              </TableAttributeNameDiv>
              <TableContainer style={{transform: 'scale(1)'}}>
                <Table stickyHeader size="small" aria-label='sticky table'>
                  <TableHead>
                    <TableRow>
                      { columnNames.map(columnName => (
                        <AttributeTableCell fSize={size} bold={bold} color={color} italic={italic} name={name}
                          yoko={yoko}>
                          {columnName}
                        </AttributeTableCell>
                      )) }
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>
            </TableDiv>
          );
        } else if (isAttribute(item) && item.cardAttributeModel === 'image') {
          return (
            <RenderImage key={ix} item={item} />
          );
        } else if (isShape(item) && item.kind === 'text') {
          const { name, color, size, bold, italic, underline, direction, orientation, yoko, tate } = getFontData(item.font);
          return (
            <TextDiv key={ix} x={item.x} y={item.y}
              width={item.width} height={item.height} rotation={item.rotation}
              frame={item.frame} $backgroundColor={item.background.display ? item.background.color : undefined}
              size={size} bold={bold} color={color} italic={italic} underline={underline} name={name}
              yoko={yoko} tate={tate} direction={direction} orientation={orientation}>
              {item.text}
            </TextDiv>
          );
        } else if (isShape(item) && item.kind === 'circle') {
          return (
            <CircleShape key={ix} item={item} />
          );
        } else if (isShape(item) && item.kind === 'rectangle') {
          return (
            <RectangleShape key={ix} item={item} />
          );
        } else if (isShape(item) && item.kind === 'balloon') {
          return (
            <BalloonShape key={ix} item={item} />
          );
        } else if (isShape(item) && item.kind === 'image') {
          return (
            <RenderImage key={ix} item={item} />
          );
        } else return undefined;
    }) }
    </CardPaper>
  );
}

export default CommonCardTemplatePreview;
